import { useState, useEffect, useMemo } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isNylPilotAdvisor, specificFundingPolicyTypeDefList } from '@shared';

export const preRegisteredFeatureFlagDefinitions: {
  key: FeatureFlagKeys;
  label: string;
  description: string;
}[] = [
  {
    key: 'wizardMode',
    label: 'Wizard Mode',
    description: 'Grant temporary wizard/admin access to this advisor',
  },
  { key: 'vbShop', label: 'VB Shop', description: 'Group policy funding page' },
  { key: 'ltciPartners', label: 'LTCI Partners', description: 'LTCI Partners disclaimers' },
  { key: 'agis', label: 'AGIS', description: 'AGIS group policy funding page' },
  {
    key: 'quoteApplyLincolnFinancial',
    label: 'Quote Apply Lincoln Financial',
    description: 'Quote Apply Lincoln Financial',
  },
  {
    key: 'quoteApplyNewYorkLife',
    label: 'Quote Apply New York Life',
    description: 'Quote Apply New York Life',
  },
  {
    key: 'quoteApplyManhattanLife',
    label: 'Quote Apply ManhattanLife',
    description: 'ManhattanLife OmniFlex Short-Term Care quoting',
  },
  {
    key: 'hideSinglePayAnd10Pay',
    label: 'Hide Single Pay and 10-Pay',
    description: 'Hide Single Pay and 10-Pay payment options in Shop Insurance filters',
  },
  {
    key: 'd2cMode',
    label: 'D2C Mode',
    description: "Enable direct-to-consumer mode for this advisor's clients",
  },
  {
    key: 'edjPilotAdvisor',
    label: 'EDJ Pilot Advisor',
    description: 'Enable EDJ pilot advisor features',
  },
  {
    key: 'edjAdvisorPilotIntakeForm',
    label: 'EDJ Advisor Pilot Intake Form',
    description: 'Enable self-test intake form for EDJ pilot advisors',
  },
  {
    key: 'genworthMode',
    label: 'Genworth Mode',
    description: 'Enable the Genworth partner experience for this advisor',
  },
  {
    key: 'nylPilotAdvisor',
    label: 'NYL Pilot Advisor',
    description:
      'Enable New York Life pilot advisor features (advisor cohort, not NYL product access)',
  },
  {
    key: 'intakeChangeApproval',
    label: 'Intake Change Approval',
    description: 'Require clients to approve advisor-proposed intake changes',
  },
  {
    key: 'nylPilotClientCreationRestricted',
    label: 'NYL Pilot Client Creation Restricted',
    description: 'Disable client creation actions for this advisor during the NYL pilot',
  },
  {
    key: 'pilotTeamOwnerView',
    label: 'Pilot Team Owner View',
    description:
      'Let this pilot advisor open the Team page, Owner view only, to invite the pilot\'s members',
  },
  {
    key: 'ccrcTypeAProducts',
    label: 'CCRC Type A Products',
    description: 'Enable Continuing Care Retirement Community Type A product modeling',
  },
  {
    key: 'financialFeasibility',
    label: 'Financial Feasibility',
    description: 'Enable the Financial Feasibility analysis in guided planning',
  },
  {
    key: 'freemiumAdvisor',
    label: 'Freemium Advisor',
    description:
      'Remove the subscription paywall at sign-up and enable the usage paywall at the financial planning page',
  },
  {
    key: 'twoFactorAuth',
    label: '2 Factor Authentication',
    description:
      "Reserve the advisor's verified phone number in Clerk for SMS second-factor sign-in",
  },
];

interface FeatureFlagsEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  warnOnNylEnrollment?: boolean;
}

export function FeatureFlagsEditor({
  value,
  onChange,
  error,
  warnOnNylEnrollment = false,
}: FeatureFlagsEditorProps) {
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [pendingNylPilotFlags, setPendingNylPilotFlags] = useState<FeatureFlags | null>(null);
  const [pendingNylClientCreationRestrictionFlags, setPendingNylClientCreationRestrictionFlags] =
    useState<FeatureFlags | null>(null);

  useEffect(() => {
    setPendingNylPilotFlags(null);
    setPendingNylClientCreationRestrictionFlags(null);
    try {
      const parsed = value ? JSON.parse(value) : {};
      setFlags(parsed);
    } catch {
      setFlags({});
    }
  }, [value]);

  const applyFlags = (newFlags: FeatureFlags) => {
    setFlags(newFlags);
    onChange(JSON.stringify(newFlags));
  };

  const handleToggle = (key: FeatureFlagKeys) => {
    const newFlags = { ...flags, [key]: !flags[key] };

    if (warnOnNylEnrollment && !isNylPilotAdvisor(flags) && isNylPilotAdvisor(newFlags)) {
      setPendingNylPilotFlags(newFlags);
      return;
    }

    if (
      warnOnNylEnrollment &&
      key === 'nylPilotClientCreationRestricted' &&
      isNylPilotAdvisor(flags) &&
      !flags.nylPilotClientCreationRestricted
    ) {
      setPendingNylClientCreationRestrictionFlags(newFlags);
      return;
    }

    applyFlags(newFlags);
  };

  const disabledProducts = useMemo(
    () => new Set(flags.disabledFundingPolicyTypes ?? []),
    [flags.disabledFundingPolicyTypes],
  );

  const productsByCarrier = useMemo(() => {
    const grouped: Record<string, typeof specificFundingPolicyTypeDefList> = {};
    for (const product of specificFundingPolicyTypeDefList) {
      const carrier = product.carrier || 'Other';
      if (!grouped[carrier]) grouped[carrier] = [];
      grouped[carrier].push(product);
    }
    return grouped;
  }, []);

  const handleProductToggle = (productId: number) => {
    const currentDisabled = flags.disabledFundingPolicyTypes ?? [];
    const isDisabled = currentDisabled.includes(productId);
    const newDisabled = isDisabled
      ? currentDisabled.filter(id => id !== productId)
      : [...currentDisabled, productId];

    const newFlags: FeatureFlags = { ...flags };
    if (newDisabled.length > 0) {
      newFlags.disabledFundingPolicyTypes = newDisabled;
    } else {
      delete newFlags.disabledFundingPolicyTypes;
    }
    setFlags(newFlags);
    onChange(JSON.stringify(newFlags));
  };

  return (
    <Card className={cn('space-y-4 p-4', error && 'border-red-500')}>
      <div className="space-y-3">
        {preRegisteredFeatureFlagDefinitions.map(({ key, label, description }) => (
          <div
            key={key}
            className="flex items-center justify-between space-x-3"
          >
            <div className="flex-1">
              <Label
                htmlFor={key}
                className="text-sm font-medium"
              >
                {label}
              </Label>
              <p className="text-xs text-gray-700">{description}</p>
            </div>
            <Switch
              id={key}
              checked={!!flags[key]}
              onCheckedChange={() => handleToggle(key)}
            />
          </div>
        ))}
      </div>

      <Collapsible className="border-t pt-4">
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <div className="text-left">
            <h4 className="text-sm font-medium">Product Access</h4>
            <p className="text-xs text-gray-500">
              Toggle off to hide a product from this advisor.
              {disabledProducts.size > 0 && (
                <span className="ml-1 font-medium text-red-600">
                  ({disabledProducts.size} disabled)
                </span>
              )}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          {Object.entries(productsByCarrier).map(([carrier, products]) => (
            <div
              key={carrier}
              className="space-y-2"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{carrier}</p>
              {products.map(product => (
                <div
                  key={product.value}
                  className="flex items-center justify-between space-x-3"
                >
                  <Label
                    htmlFor={`product-${product.value}`}
                    className="text-sm"
                  >
                    {product.label}
                  </Label>
                  <Switch
                    id={`product-${product.value}`}
                    checked={!disabledProducts.has(product.value)}
                    onCheckedChange={() => handleProductToggle(product.value)}
                  />
                </div>
              ))}
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
      <AlertDialog
        open={pendingNylPilotFlags !== null}
        onOpenChange={open => {
          if (!open) setPendingNylPilotFlags(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable NYL Pilot Advisor?</AlertDialogTitle>
            <AlertDialogDescription>
              Enabling the NYL pilot will override this advisor&apos;s healthcare inflation and
              client education guide defaults. Client results sharing will also be locked to Manual
              Sharing while the flag is enabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => {
                  if (pendingNylPilotFlags) applyFlags(pendingNylPilotFlags);
                  setPendingNylPilotFlags(null);
                }}
              >
                Enable NYL Pilot
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingNylClientCreationRestrictionFlags !== null}
        onOpenChange={open => {
          if (!open) setPendingNylClientCreationRestrictionFlags(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restrict Client Creation Options</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Enabling this flag will restrict:</p>
                <ul className="my-2 list-disc space-y-1 pl-5">
                  <li>Plan for Yourself and Fill Intake Form</li>
                  <li>Invite Client and Add Clients</li>
                  <li>Intake Form Link</li>
                  <li>New Case Quick Start</li>
                  <li>Add New Partner</li>
                  <li>Opening the Invite Client dialog from a saved URL</li>
                </ul>
                <p>
                  It will also hide the Create Your First Plan onboarding step. Link Existing Client
                  as Partner will remain available.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => {
                  if (pendingNylClientCreationRestrictionFlags) {
                    applyFlags(pendingNylClientCreationRestrictionFlags);
                  }
                  setPendingNylClientCreationRestrictionFlags(null);
                }}
              >
                Enable Restriction
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
